import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RedirectCommand,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { ToastBarService } from '../services/toast-bar.service';
import { AccountInfoService } from '../services/account-info.service';
import { filter, map, take } from 'rxjs';

/**
 * Guard that checks if the current user has any required roles specified in the route's `allowedRoles` data property.
 *
 * If the user possesses any of the required roles, access is granted. Otherwise, a toast notification is shown and the user is redirected to the root path.
 *
 * @param route - The activated route snapshot containing route data.
 * @param state - The router state snapshot.
 * @returns An Observable that emits `true` if access is allowed, or a `RedirectCommand` if access is denied.
 *
 * @remarks
 * - Uses dependency injection to access `Router`, `ToastBarService`, and `AccountInfoService`.
 * - Subscribes to `userRolesData$` from `AccountInfoService` to get the current user's roles.
 * - Only the first valid emission is processed.
 */
export const RoleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const toastBar = inject(ToastBarService);
  const accountInfoService = inject(AccountInfoService);

  const allowedRoles: string[] = route.data['allowedRoles'];

  return accountInfoService.userRolesData$.pipe(
    filter((roles) => roles !== null && roles.length > 0),
    map((roles) => {
      // Check if user has any required role
      const hasAnyRequired = roles.some((role) => allowedRoles.includes(role));

      if (hasAnyRequired) {
        return true;
      } else {
        // Else, user doesn't have any required role
        toastBar.showToast({
          severity: 'error',
          summary: 'Error',
          detail: "You don't have the required role!",
        });
        return false;
      }
    }),
    take(2) // Complete after first emission
  );
};
